import { c as _c } from "react/compiler-runtime";
import { useQuery } from '@apollo/client/react';
import { Box, Center, Separator, Heading, HStack, Image, SimpleGrid, Spinner, Text, VStack } from '@chakra-ui/react';
import { MarkdownRenderer } from '@src/components/sco/MarkdownRenderer';
import { UserAccessGate } from '@src/components/sco/UserAccessGate';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { PrettyPrintJSON } from '@src/components/sco/utilities/PrettyPrintJSON';
import { useActiveContentContext } from '@src/contexts/ActiveContentContext';
import { ScoPlaybook, ScoPlaybookPage, ScoPlaybookSection } from '@src/graphql-types';
import { useUniversalEffect } from '@src/hooks/useUniversalEffect';
import { gql } from '@src/lib/gql';
import { useQueryState } from 'nuqs';
import React from 'react';
type PlaybookSectionDetailProps = {
  playbookSlug: string;
  playbookSectionSlug: string;
};
type PlaybookItem = {
  type: string;
  content?: PlaybookItem[];
  children?: PlaybookItem[];
};
const PRINT_JSON = false;
export const PlaybookSectionDetail = props => {
  const $ = _c(21);
  const activeContent = useActiveContentContext();
  useQueryState("prompt");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = gql`
      query ($playbookSlug: String!) {
        scoPlaybook(slug: $playbookSlug) {
          id
          color
          illustration
          learningPoints
          sections {
            id
            badge
            illustration
            slug
            title
            pages {
              id
              title
              content
            }
            teaser
            learningPoints
          }
          seoTitle
          seoDescription
          slug
          teaser
          title
          tldr
          topic {
            id
            colorHexCode
          }
        }
      }
    `;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props.playbookSlug) {
    t1 = {
      variables: {
        playbookSlug: props.playbookSlug
      }
    };
    $[1] = props.playbookSlug;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const query = useQuery(t0, t1);
  const playbook = query.data?.scoPlaybook;
  let t2;
  if ($[3] !== playbook || $[4] !== props.playbookSectionSlug) {
    t2 = playbook && playbook.sections.find(s => s.slug === props.playbookSectionSlug);
    $[3] = playbook;
    $[4] = props.playbookSectionSlug;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const playbookSection = t2;
  let t3;
  if ($[6] !== activeContent || $[7] !== playbook || $[8] !== playbookSection) {
    t3 = () => {
      activeContent.setSiteSection("playbooks.single.phase");
      activeContent.setBreadcrumbs([{
        title: "Playbooks",
        href: "/playbooks"
      }, {
        title: playbook ? playbook.title : "Loading...",
        href: playbook ? `/playbooks/${playbook.slug}` : "#"
      }, {
        title: playbookSection ? playbookSection.title : "Loading...",
        href: playbookSection ? `/playbooks/${playbook.slug}/${playbookSection.slug}` : "#"
      }]);
    };
    $[6] = activeContent;
    $[7] = playbook;
    $[8] = playbookSection;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = playbook?.id;
  const t5 = playbookSection?.id;
  let t6;
  if ($[10] !== t4 || $[11] !== t5) {
    t6 = [t4, t5];
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  useUniversalEffect(t3, t6);
  let t7;
  if ($[13] !== playbookSection) {
    t7 = !playbookSection && <Center padding="1em"><Spinner /></Center>;
    $[13] = playbookSection;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== playbook || $[16] !== playbookSection) {
    t8 = playbookSection && <><Box bgColor={playbook.topic?.colorHexCode ? `${playbook.topic?.colorHexCode}50` : "blackAlpha.500"} paddingX="1em" paddingY={["1em", "2em", "4em"]}><SimpleGrid alignItems="center" columns={[1, 1, 2]} margin="0 auto" maxWidth="780px" width="full"><VStack align="start" gap={5}><Text asChild={true} color="colorPalette.fg/50" textStyle="label"><NextLink href={`/playbooks/${playbook.slug}`}>{playbook.title} {"Playbook: "}{playbookSection.badge}</NextLink></Text><Heading fontSize="3xl">{playbookSection.title}</Heading><Text color="colorPalette.fg" fontFamily="longform" fontSize="md">{playbookSection.teaser}</Text></VStack><Box textAlign="center"><Image src={playbookSection.illustration} width="100%" /></Box></SimpleGrid></Box><Box margin="0 auto" maxWidth="780px" paddingX="1em" paddingY={["1em", "2em", "4em"]} width="full"><Box margin="0 auto" maxWidth="780px"><UserAccessGate ctaVariant="boxed-card" require={{
            contentPermissions: ["canAccessCourses"]
          }} ctaProps={{
            boxProps: {
              bgColor: "actionActiveSecondary",
              marginBottom: "2em"
            },
            title: "Upgrade to unlock this entire playbook."
          }} /></Box><VStack align="stretch" width="full">{playbookSection.pages.map(_temp)}</VStack><UserAccessGate ctaVariant="boxed-card" require={{
          contentPermissions: ["canAccessCourses"]
        }} ctaProps={{
          boxProps: {
            bgColor: "actionActiveSecondary",
            marginY: "4em"
          },
          title: "Finding this playbook helpful?"
        }} /></Box></>;
    $[15] = playbook;
    $[16] = playbookSection;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
const renderPage = (page: ScoPlaybookPage, pageIndex: number) => {
  return <Box key={pageIndex}>
      <a id={`page-${page.id}`} />
      <Heading fontSize={'2em'} paddingY={'0.5em'}>
        {page.title}
      </Heading>
      {page.content.map((item, itemIndex: number) => {
      return <Box key={itemIndex}>{renderItem(item, itemIndex)}</Box>;
    })}
    </Box>;
};
const renderItem = (item, index) => {
  const Component = ItemComponents[item.type];
  if (Component) {
    return <Component key={index} type={item.type} {...item.props} children={item.children} />;
  } else {
    return <Box key={index} bgColor={'red'} padding={'1em'}>
        {'MISSING TYPE: '}
        {item.type}
      </Box>;
  }
};
const ItemComponents = {
  Block: (props: {
    children?: {
      type: string;
    }[];
  }) => <>
      {typeof props.children === 'string' ? <>
          {'STRING: '}
          {props.children}
        </> : <VStack align={'stretch'} gap={'2em'}>
          {props.children.map((item, index) => renderItem(item, index))}
        </VStack>}
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  Container: (props: {
    children?: any[];
  }) => <>
      {typeof props.children === 'string' ? <>
          {'STRING: '}
          {props.children}
        </> : <VStack align={'stretch'} gap={'2em'}>
          {props.children.map((item, index) => renderItem(item, index))}
        </VStack>}
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  ArticleReader: (props: {
    title?: string;
    children: any;
  }) => <>
      {props.title && <Heading marginTop={'1em'}>{props.title}</Heading>}
      <MarkdownRenderer preset={'edu'}>{props.children[0]}</MarkdownRenderer>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  CoursePhase: (props: {
    phase?: string;
    title?: string;
    link?: {
      to?: string;
    };
    summary?: string;
  }) => <>
      <Box bgColor={'blackAlpha.500'} borderRadius={'0.5em'} padding={'2em'}>
        <Text textStyle={'labelXs'}>{(props.phase || '').toUpperCase()}</Text>
        <Heading as={'h3'} marginTop={'0.25em'}>
          <NextLink href={(props.link?.to || '#').replace('/library/playbooks', '/playbooks')}>
            {props.title}
          </NextLink>
        </Heading>
        <MarkdownRenderer>{props.summary}</MarkdownRenderer>
      </Box>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  CourseProCon: (props: {
    pros?: string;
    cons?: string;
  }) => <>
      <SimpleGrid columns={[1, 1, 1, 2]} gap={'1em'} paddingBottom={'2em'}>
        <Box bgColor={'blackAlpha.500'} borderRadius={'0.5em'} padding={'2em'}>
          <Heading>Pros</Heading>
          <MarkdownRenderer preset={'edu'}>{props.pros}</MarkdownRenderer>
        </Box>
        <Box bgColor={'blackAlpha.500'} borderRadius={'0.5em'} padding={'2em'}>
          <Heading>Cons</Heading>
          <MarkdownRenderer preset={'edu'}>{props.cons}</MarkdownRenderer>
        </Box>
      </SimpleGrid>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  KeyTakeaway: (props: {
    text: string;
  }) => <>
      <Box bgColor={'blackAlpha.500'} borderRadius={'0.5em'} marginY={'2em'} padding={'2em'}>
        <Heading>Key Takeaway</Heading>
        <MarkdownRenderer preset={'edu'}>{props.text}</MarkdownRenderer>
      </Box>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  ImageList: (props: {
    items?: Array<{
      badge?: string;
      imageUrl?: string;
      text?: string;
      isHTML?: boolean;
    }>;
  }) => <>
      <VStack align={'stretch'} gap={'2em'}>
        {props.items.map((item, index) => <HStack key={index} gap={'1em'}>
            <Box textAlign={'center'} width={'80px'}>
              <Image maxWidth={'100%'} src={item.imageUrl} />
            </Box>
            <Box width={'full'}>
              {item.isHTML ? <MarkdownRenderer preset={'edu'}>{item.text}</MarkdownRenderer> : <Text>{item.text}</Text>}
            </Box>
          </HStack>)}
      </VStack>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  Spotlight: (props: {
    title?: string;
    subtitle?: string;
    illustration?: string;
    text?: string;
  }) => <>
      <Box bgColor={'blackAlpha.500'} borderRadius={'0.5em'} marginY={'2em'} padding={'2em'}>
        <Heading>
          {'Spotlight: '}
          {props.title}
        </Heading>
        <Text margin={'0 !important'} padding={'0 !important'}>
          {props.subtitle}
        </Text>
        <MarkdownRenderer preset={'edu'}>{props.text}</MarkdownRenderer>
      </Box>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  Image: (props: {
    imageUrl?: string;
  }) => <>
      <Center>
        <Image src={props.imageUrl} width={['90%', '80%', '50%']} />
      </Center>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  ProTip: (props: {
    text?: string;
  }) => <>
      <Box bgColor={'blackAlpha.500'} borderRadius={'0.5em'} marginY={'2em'} padding={'2em'}>
        <Heading>Pro Tip</Heading>
        <MarkdownRenderer preset={'edu'}>{props.text}</MarkdownRenderer>
      </Box>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  FoodForThought: (props: {
    text?: string;
  }) => <>
      <Box bgColor={'blackAlpha.500'} borderRadius={'0.5em'} marginY={'2em'} padding={'2em'}>
        <Heading>Food For Thought</Heading>
        <MarkdownRenderer preset={'edu'}>{props.text}</MarkdownRenderer>
      </Box>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  CourseNote: (props: {
    text?: string;
  }) => <>
      <Box bgColor={'blackAlpha.500'} borderRadius={'0.5em'} marginY={'2em'} padding={'2em'}>
        <Heading>Take Note:</Heading>
        <MarkdownRenderer preset={'edu'}>{props.text}</MarkdownRenderer>
      </Box>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>,
  Divider: (props: {
    children?: {
      props?: {
        imageUrl?: string;
      };
    }[];
  }) => <>
      <HStack gap={'2em'}>
        <Separator flexGrow={1} />
        <Image maxWidth={'80px !important'} src={props.children[0].props.imageUrl} />
        <Separator flexGrow={1} />
      </HStack>
      {PRINT_JSON && <PrettyPrintJSON data={props} />}
    </>
};
function _temp(page, pageIndex) {
  return renderPage(page, pageIndex);
}